<template>
  <div :class="classes">
    <label v-if="label.length > 0" class="text-gray-700 font-semibold text-base ml-2 mb-1 inline-block">{{ label }}</label>
    <form-antd-select
      v-bind="$attrs"
      :searchable="true"
      :showLabels="false"
      :options="
        employeesList.map((item) => {
          item.value = item.empid
          item.label = customLabel(item)
          return item
        })
      "
      :value="_value"
      @update:value="returnInput"
      :placeholder="placeholder"
      @search="loadEmployeeList"
      :filterOption="false"
    >
      <template #noResult> No matches found. </template>

      <template #noOptions> Type name to generate results. </template>

      <template #placeholder>
        <div class="flex -ml-7 text-gray-400">
          <UserIcon class="mr-2 text-gray-600" style="pointer-events: none; margin-top: -3px" />
          {{ placeholder }}
        </div>
      </template>

      <template #singleLabel="{ option }">
        <template v-if="customLabelSlot">
          <slot name="customLabel" :selected="option" />
        </template>
        <template v-else-if="size == 'large'">
          <div class="flex -ml-7">
            <div class="flex">
              <img :src="`//worklife.scrippsnet.com/utils/photo/75/${option.empid}/1594180800.jpg`" class="rounded-full w-10 h-10" style="margin-top: 3px" />
            </div>
            <div class="flex flex-col leading-tight ml-4 -mt-1">
              <div class="text-base font-semibold">{{ option.first_name }} {{ option.last_name }}</div>
              <div class="text-sm font-normal text-gray-700">{{ option.title }}</div>
              <div class="text-sm text-gray-600">{{ option.location_descr }}</div>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="flex -ml-7">
            <user-icon class="mr-2 text-gray-600" style="pointer-events: none" />
            {{ option.first_name }} {{ option.last_name }}
          </div>
        </template>
      </template>
    </form-antd-select>
  </div>
</template>

<script>
import api from '../../lib/api'
import { default as FeatherIcons } from '../feather-icons-wrapper'
import FormAntdSelect from './AntDesign/FormAntdSelect.vue'
const { UserIcon } = FeatherIcons

export default {
  name: 'FormEmployeeLookup',
  inheritAttrs: false,
  components: {
    FormAntdSelect,
    UserIcon
  },
  data() {
    return {
      employeesList: [],
      selected: {}
    }
  },
  props: {
    /**
     * Current employee object.
     * @model
     */
    modelValue: {
      type: [Number, String],
      default: () => {}
    },
    /**
     * Custom array of employee ids instead of the default list of all employees.
     */
    empid: {
      type: Array,
      required: false,
      default: () => []
    },
    /**
     * Optional label above the employee picker.
     */
    label: {
      type: String,
      default: ''
    },
    /**
     * Custom array of location filters for employees.
     */
    locations: {
      type: Array,
      required: false,
      default: () => []
    },
    /**
     * Custom array of department filters for employees.
     */
    departments: {
      type: Array,
      required: false,
      default: () => []
    },
    /**
     * Specify `contractor` or `employee` to restrict to specific employee type.  The `all` option
     * can be specified for both types or you can not include this property.  The default is all employee types.
     */
    employeeType: {
      type: String,
      required: false,
      default: 'all'
    },
    /**
     * Sepcify fields returned for the selected employee. Defaults to empid, first_name, and last_name.
     */
    fields: {
      type: Array,
      required: false,
      default: () => ['empid', 'first_name', 'last_name', 'title', 'location_descr', 'department_descr']
    },
    placeholder: {
      type: String,
      default: 'Find employee'
    },
    classes: {
      type: String,
      default: ''
    },
    /**
     * Specify either 'small' or 'large' to set the details of the component.
     */
    size: {
      type: String,
      default: 'small'
    }
  },
  emits: ['update:modelValue', 'change'],
  watch: {
    modelValue: function (newValue, oldValue) {
      // console.log(newValue, this._value?.empid)
      if (newValue?.toString().length > 4 && newValue != this._value?.empid) {
        this.loadEmployeeInfo(newValue)
      }
    }
  },
  created() {
    this.baseDomain = location.hostname.split('.').slice(-2).join('.')

    if (this.modelValue) {
      this.loadEmployeeInfo(this.modelValue)
    }
  },
  computed: {
    _value() {
      // check for the employee in the current array
      let employee = this.employeesList.find((item) => item.empid == this.modelValue)
      // return employee ?? {}
      return employee
    },
    customLabelSlot() {
      return !!(this.$slots.customLabel && this.$slots.customLabel())
    }
  },
  methods: {
    customLabel(employee) {
      return `${employee.first_name} ${employee.last_name}`
    },
    returnInput(value) {
      if (value) {
        /**
         * This will return the employee id.
         * @event input
         */
        this.$emit('update:modelValue', value.empid)
      } else {
        this.$emit('update:modelValue', null)
      }

      /**
       * This will return the employee object. This object can be defined using the **fields** prop.
       * @event change
       * @property {number} empid
       * @property {string} first_name
       * @property {string} last_name
       */
      this.$emit('change', value)
    },
    async loadEmployeeList(searchTerm) {
      // console.log('loadEmployeeList', this.modelValue, this._value, searchTerm)
      // do not do search unless there are at least three characters
      if (searchTerm.length < 3) {
        return
      }

      // Set props filters
      const fields = this.getPropsArrayFilter('fields', this.fields)
      const empids = this.getPropsArrayFilter('empid', this.empid, ':')
      const locations = this.getPropsArrayFilter('locations', this.locations)
      const departments = this.getPropsArrayFilter('departments', this.departments)
      const employeeType = this.getPropsStringFilter('employeeType', this.employeeType)

      const props_filters = fields + empids + locations + departments + employeeType
      // console.log('props_filters', props_filters)

      const response = await api.get(`/v2/employees?limit=2500${props_filters}&keywords=` + searchTerm)
      this.employeesList = response.data.results
    },
    async loadEmployeeInfo(id) {
      const response = await api.get(`/v2/employees/${id}`)
      // console.log('loadEmployeeInfo', id, response.data)
      this.employeesList = [response.data]
    },
    getPropsArrayFilter(paramName, prop, operator = '') {
      if (prop.length > 0) {
        return `&${paramName}${operator}=` + prop.join()
      }
      return ''
    },
    getPropsStringFilter(paramName, prop, operator = '') {
      if (prop.length > 0) {
        // Employee type
        if (paramName == 'employeeType') {
          if (prop == 'employee') {
            return '&empid<=900000'
          } else if (prop == 'contractor') {
            return '&empid>=899999'
          }
        }
      }
      return ''
    }
  }
}
</script>

<style lang="postcss" scoped></style>
